/**
 * @file Velocity component: linear motion plus the intent that drives it.
 *
 * `desired` is written by controllers (player input, AI) and converted into
 * `linear` by the movement system, which is what makes acceleration and
 * friction a single shared rule instead of duplicated logic per controller.
 */

import * as THREE from 'three';
import { Component } from '../ecs/Component.js';

export class Velocity extends Component {
  /**
   * @param {number} [maxSpeed] Speed cap in units/second.
   * @param {number} [acceleration] Approach rate toward `desired`.
   * @param {number} [deceleration] Approach rate toward zero when idle.
   */
  constructor(maxSpeed = 0, acceleration = 0, deceleration = 0) {
    super();
    /** @type {THREE.Vector3} Current velocity, units/second. */
    this.linear = new THREE.Vector3();
    /** @type {THREE.Vector3} Target velocity requested by a controller. */
    this.desired = new THREE.Vector3();
    /**
     * External shove, units/second, kept apart from {@link linear} on purpose:
     * steering pulls `linear` back to `desired` and clamps it to `maxSpeed`
     * every frame, which would swallow a bullet impact whole (issue #75). This
     * is integrated as-is and bled off by the movement system.
     * @type {THREE.Vector3}
     */
    this.knockback = new THREE.Vector3();
    /** @type {number} */
    this.maxSpeed = maxSpeed;
    /** @type {number} */
    this.acceleration = acceleration;
    /** @type {number} */
    this.deceleration = deceleration;
    /** @type {boolean} When false the movement system integrates `linear` as-is. */
    this.steered = true;
  }

  /** @returns {void} */
  reset() {
    this.linear.set(0, 0, 0);
    this.desired.set(0, 0, 0);
    this.knockback.set(0, 0, 0);
    this.steered = true;
  }
}
